import moment from "moment";
import {read, utils, writeFile} from 'xlsx';
import {Environment, GeneralAppInfo} from "./constants";
import {PermissionsEnum} from "./permissions.enum";

export const setKey = key => {
	return `${getEnvironment()}-${key}`
};

export const getYear = () => {
	const date = new Date();
	return date.getFullYear();
};

export const getDay = (value) => {
	let date = new Date();
	date.setDate(date.getDate() + value);
	return date;
};

export const getDayRange = (value) => {
	const startDate = moment(getDay(value)).startOf('date').endOf('hour').toISOString();
	const endDate = moment(getDay(value)).endOf('date').endOf('hour').toISOString();
	return {startDate, endDate}
};

export const getWeek = (difference) => {
	let date = new Date();
	date.setDate(date.getDate() - date.getDay() + difference);
	return date;
};

export const getWeekRange = (startValue) => {
	const startDate = moment(getWeek(startValue)).endOf('date').toISOString();
	const endDate = moment(getWeek(startValue + 7)).endOf('date').toISOString();
	return {startDate, endDate}
};

export const getMonthName = (value) => {
	const month = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
	return month[value];
};

export const createInvoiceGenerationRequestBody = (requestBody = {}) => {
	const generationRequestBody = {...requestBody};
	const hasValue = value => value !== undefined && value !== null && value !== "";
	const hasNonYearGenerationPeriod = [
		generationRequestBody.start_date,
		generationRequestBody.end_date,
		generationRequestBody.month,
		generationRequestBody.quarter,
		generationRequestBody.half_year,
		generationRequestBody.reference,
	].some(hasValue);

	if(hasNonYearGenerationPeriod) delete generationRequestBody.year;
	else if(hasValue(generationRequestBody.year)) generationRequestBody.year = `${generationRequestBody.year}`;

	if(hasValue(generationRequestBody.month) && Number.isFinite(Number(generationRequestBody.month))) {
		const monthIndex = Number(generationRequestBody.month) - 1;
		if(monthIndex >= 0 && monthIndex < 12) generationRequestBody.month = getMonthName(monthIndex);
	}

	return generationRequestBody;
};

const getInvoiceSaveYear = (requestBody = {}, fallbackDate) => {
	if(requestBody.year) return requestBody.year;
	if(requestBody.start_date) return new Date(requestBody.start_date).getFullYear().toString();
	if(fallbackDate) return new Date(fallbackDate).getFullYear().toString();
	return new Date().getFullYear().toString();
};

const getInvoiceSaveMonth = month => {
	if(month === undefined || month === null || month === "") return 0;
	const numericMonth = Number(month);
	if(Number.isFinite(numericMonth)) return numericMonth;
	const monthIndex = moment.months().findIndex(each => each.toLowerCase() === `${month}`.toLowerCase());
	return monthIndex === -1 ? 0 : monthIndex + 1;
};

const getInvoiceSaveDay = day => {
	if(day === undefined || day === null || day === "") return 0;
	const numericDay = Number(day);
	return Number.isFinite(numericDay) ? numericDay : 0;
};

export const createInvoiceSavePayload = (requestBody = {}, invoiceType, invoiceBody = {}) => ({
	year: Number(getInvoiceSaveYear(
		requestBody,
		invoiceBody?.scheduled_date || invoiceBody?.created_at || invoiceBody?.date
	)),
	...(requestBody.month !== undefined && requestBody.month !== null && requestBody.month !== ""
		? {month: getInvoiceSaveMonth(requestBody.month)}
		: {}),
	...(requestBody.day !== undefined && requestBody.day !== null && requestBody.day !== ""
		? {day: getInvoiceSaveDay(requestBody.day)}
		: {}),
	...(requestBody.start_date ? {start_date: requestBody.start_date} : {}),
	...(requestBody.end_date ? {end_date: requestBody.end_date} : {}),
	request_body: {...requestBody},
	invoice_type: invoiceType,
	invoice_body: invoiceBody,
});

export const yearPicker = startRange => {
	const date = new Date();
	let array = [];
	while(startRange <= date.getFullYear()) {
		const newObject = {label: startRange, value: startRange.toString()}
		array.unshift(newObject);
		startRange++
	}
	return array;
};

export const copyToClipboard = async text => {
	return await navigator.clipboard.writeText(text);
}

export const getEnvironment = () => {
	let env;
	if(window.location.hostname === GeneralAppInfo.LOCALHOST) env = Environment.LOCAL;
	else if(window.location.hostname === GeneralAppInfo.BETA_BASEURL_SIJU) env = Environment.SANDBOX;
	else if(window.location.hostname === GeneralAppInfo.PROD_BASEURL_SIJU) env = Environment.PRODUCTION;
	return env;
};

export const formatNumber = value => {
	const numFormat = Intl.NumberFormat('en-US');
	numFormat.format(value);
	return numFormat.format(value);
};

export const maskString = str => {
	if(!str || str.length === 0) return "";
	const lastUnderscoreIndex = str.lastIndexOf('_');
	const prefix = str.substring(0, lastUnderscoreIndex + 1);
	const maskedString = str.substring(lastUnderscoreIndex + 1, str.length - 5).replace(/\w/g, "#");
	const suffix = str.substring(str.length - 5);
	return `${prefix}${maskedString}${suffix}`
}


export const formatSubscriptionPlan = payloadData => {
	let colourMaps = ['primary-gradient', 'warning-gradient', 'success-gradient', 'danger-gradient', 'info-gradient'];
	return payloadData.map((item, index) => ({
		...item,
		label: item['name'],
		fill: index > colourMaps.length ? colourMaps[Math.floor(Math.random() * 5)] : colourMaps[index],
	}));
};

export const colorMap = payloadData => {
	let colourMaps = ['primary', 'secondary', 'info', 'warning', 'dark', 'success', 'danger'];
	return payloadData.map((item, index) => ({
		...item,
		fill: index > colourMaps.length ? colourMaps[Math.floor(Math.random() * 7)] : colourMaps[index],
	}));
};

export const toTitleCase = str => {
	if(!str || str.length === 0) return "";
	str = (/[_&\\/#, +()$~%.'":*?<>{}-]/g).test(str) ? str.replaceAll(/[_&\\/#, +()$~%.'":*?<>{}-]/g, ' ') : str;
	return str.trim().split(' ').map(word => (
		word[0] ? word[0].toUpperCase() + word.substring(1).toLowerCase() : word + word.substring(1).toLowerCase()
	)).join(' ')
}

export const formatUser = payloadData => {
	return payloadData.map(item => ({
			...item,
			label: `${toTitleCase(item['first_name'])} ${toTitleCase(item['last_name'])}`,
			value: item.id,
		})
	);
};

export const formatCoupons = payloadData => {
	if(!payloadData || payloadData.length === 0) return [];
	else return payloadData.map(item => ({
		...item,
		label: `${item.code} - ${item.discount}%`,
		value: item.code
	}));
};

export const formatBusinesses = payloadData => {
	if(!payloadData || payloadData.length === 0) return [];

	else return payloadData.map(item => ({
		...item,
		label: item['name'],
		value: item['id'],
	}));
};

export const formatProducts = payloadData => {
	if(!payloadData || payloadData.length === 0) return [];

	else return payloadData.map(item => ({
		...item,
		label: item['name'],
		value: item['sku'],
	}));
};

export const formatOrganisation = payloadData => {
	if(!payloadData || payloadData.length === 0) return [];

	else return payloadData.map(item => ({
		...item,
		label: item['name'],
		value: item['reference'],
	}));
};

export const formatTripPoolPricingConfig = (payloadData, organisationRef) => {
	let organisationTripPoolConfig = [], subOrganisationTripPoolConfig = [];
	for(let data of payloadData) {
		if(data.organisation_info.reference === organisationRef) organisationTripPoolConfig.push(data);
		else subOrganisationTripPoolConfig.push(data);
	}
	return {organisationTripPoolConfig, subOrganisationTripPoolConfig};
};

export const formatTripTypes = payloadData => {
	return payloadData.map(item => ({
			...item,
			label: !item.system_name.startsWith('DISTRIBUTION') ? `${item.customized_name} (${item.system_name} MODEL)` : item.customized_name ? item.customized_name : item.system_name,
			value: item.customized_name
		})
	);
};

export const formatSubOrganisation = (payloadData, type) => {
	if(!payloadData || payloadData.length === 0) return [];

	else if(type === "ALL") {
		let subOrganisation = [], organisation = [];

		for(const item of payloadData) {
			if(item.is_sub_organisation) subOrganisation.push(item);
			else organisation.push(item);
		}
		/*
		// THIS IS TO EXTRACT UNIQUE VALUES FROM AN ARRAY WITHOUT DUPLICATES
		 return Array.from(new Map(payloadData.map(item => [item.sub_organisation.reference, item])).values());
		*/
		return {subOrganisation, organisation}
	} 
	else return payloadData.filter(item => item["is_sub_organisation"]);
};

export const formatSubOrganisationTripTypes = payloadData => {
	return payloadData.map(item => ({label: item.key, value: item.key}));
};

export const formatBookingTripType = payloadData => {
	const newArray = payloadData.map(item => ({
			label: item.customized_name ? toTitleCase(item.customized_name) : toTitleCase(item.system_name),
			value: item.system_name, filter: `&system_trip_type=${item.system_name}`
		})
	);
	return [{label: 'All Trips', value: "ALL", filter: ""}, ...newArray]
};

export const formatOrderSets = payloadData => {
	let formattedOrderSets = [];
	if(!payloadData || payloadData.length === 0) return formattedOrderSets;

	for(let item of payloadData) {
		if(item.status === "PREPARED") {
			const address = (item.merchant && item.merchant.address && (item.merchant.address.name || item.merchant.address.city || item.merchant.address.address)) ? (item.merchant.address.name || item.merchant.address.city || item.merchant.address.address) : "";
			const store = (item.merchant && item.merchant.extras && item.merchant.extras.name) ? `- ${item.merchant.extras.name} - ` : "";
			const reference = item.reference, added_on = item.created_at ?? Date.now();
			const label = `${reference} ${store} ${address} (${moment(added_on).format("lll")})`
			formattedOrderSets.push({...item, label: label ?? item.reference, value: item.reference});
		}
	}
	return formattedOrderSets;
};

export const formatTripTypeByCategory = (tripTypes, type) => {
	let businessCategory = [], customerCategory = [];
	for(let tripType of tripTypes) {
		if(tripType.system_name.startsWith('DISTRIBUTION') && type === 'BUSINESS') {
			const formatData = {
				...tripType,
				label: tripType.customized_name ? tripType.customized_name : tripType.system_name,
				value: tripType.customized_name
			}
			businessCategory.push(formatData);
		}
		if(!tripType.system_name.startsWith('DISTRIBUTION') && type === 'CUSTOMER') {
			const formatData = {
				...tripType,
				label: tripType.customized_name ? `${tripType.customized_name} (${tripType.system_name} MODEL)` : tripType.system_name,
				value: tripType.customized_name
			}
			customerCategory.push(formatData)
		}
	}
	if(type === 'BUSINESS') return businessCategory;
	else return customerCategory;
};

export const formatDynamicAuthOptions = payloadData => {
	let dynamicField = payloadData.map(item => ({...item, label: toTitleCase(item.key), value: item.key}));
	return [{label: 'Email', value: 'email'}, ...dynamicField];
};

export const formatReconciliationOrder = payloadData => {
	if(!payloadData) return [];

	return payloadData.map(item => {
		// let reconKey = item.type === 'PRODUCT' ? 'sku' : 'catalogue';
		// // let reconValue = item.type === 'PRODUCT' ? item['sku'] : item['catalogue'];
		// return ({[reconKey]: reconValue, reconciled: item.reconciled.toString()})
		return ({id: item.id, reconciled: item.reconciled.toString()});
	});
}

export const formatBillingInvoice = payloadData => {
	const {quote: data} = payloadData;
	let newArray = [['Name', 'Description', 'Amount']];
	for(const eachItem in data) {
		let entry = [`${data[eachItem]['name']}`, `${data[eachItem]['type']}`, `${data[eachItem].currency} ${formatAmountToString(data[eachItem]['amount'])}`];
		newArray.push(entry);
	}
	return newArray;
};

export const getTripCostOfService = trip => {
	const costOfService = [
		trip?.cost_of_service,
		trip?.costOfService,
		trip?.trip_amount,
	].find(value => value !== undefined && value !== null && value !== "");
	return costOfService === "-" || costOfService === undefined ? "" : costOfService;
};

export const formatBusinessInvoiceRate = (data, currency, type) => {
	let newArray;
	const getRateTripTotal = rate => {
		const tripsData = Array.isArray(rate?.tripsData) ? rate.tripsData : [];
		if(tripsData.length === 0) return getNumericInvoiceValue(rate?.amount);
		return tripsData.reduce((sum, trip) => (
			sum + getTripInvoiceTotal({
				trip_amount: trip?.trip_amount,
				extra_stop_amount: trip?.total_stop_amount,
			})
		), 0);
	};

	if(type && type === "EXTENSIVE") {
		newArray = [[
			'Date - Trip Scheduled',
			'Waybill Number',
			'Stops',
			`VAT ${currency ? `(${currency})` : ""}`,
			`Cost of Service ${currency ? `(${currency})` : ""}`,
			`Extra Stop Amount ${currency ? `(${currency})` : ""}`,
			`Trip Total ${currency ? `(${currency})` : ""}`,
		]]
		for(let eachItem of data["rates"]) {
			const tripRows = Array.isArray(eachItem?.tripsData) && eachItem.tripsData.length > 0
				? eachItem.tripsData
				: [eachItem];
			for(let trip of tripRows) newArray.push([
				eachItem.date,
				getDetailedBusinessInvoiceWaybillNumber(trip),
				trip.stops ? trip.stops : "-",
				trip.vat ? formatAmountToString(trip.vat) : "- ",
				getTripCostOfService(trip) !== "" ? formatAmountToString(getTripCostOfService(trip)) : "-",
				trip.total_stop_amount !== undefined && trip.total_stop_amount !== null ? formatAmountToString(trip.total_stop_amount) : "-",
				formatAmountToString(getTripInvoiceTotal({
					trip_amount: trip.trip_amount,
					extra_stop_amount: trip.total_stop_amount,
				}))
			]);
		}
	} else {
		newArray = [['Date - Trip Scheduled', 'Number of trips', 'Amount']]
		for(let eachItem of data["rates"]) newArray.push([eachItem.date, eachItem.trips, formatAmountToString(getRateTripTotal(eachItem))]);
	}
	return newArray;
};

const cleanInvoiceText = value => {
	if(value === undefined || value === null) return "";
	if(typeof value === "string") return value.replace(/<[^>]*>?/gm, " ").replace(/\s+/g, " ").trim();
	return `${value}`;
};

const cleanInvoiceAddress = value => {
	if(value === undefined || value === null || value === "") return "";
	if(typeof value === "string") return cleanInvoiceText(value);
	if(Array.isArray(value)) return value.map(cleanInvoiceAddress).filter(Boolean).join(", ");
	if(typeof value === "object") {
		const directAddress = getFirstAvailableValue(
			value.address,
			value.location,
			value.street,
			value.full_address,
			value.formatted_address
		);
		if(directAddress) return cleanInvoiceAddress(directAddress);
		return Object.values(value)
			.filter(each => each !== undefined && each !== null && each !== "")
			.map(cleanInvoiceText)
			.filter(Boolean)
			.join(", ");
	}
	return cleanInvoiceText(value);
};

const getFirstAvailableValue = (...values) => {
	for(const value of values) {
		if(value !== undefined && value !== null && value !== "") return value;
	}
	return "-";
};

const normalizeInvoiceWaybill = value => {
	const waybill = cleanInvoiceText(value);
	return waybill.toLowerCase() === "nil" ? "" : waybill;
};

const addInvoiceWaybill = (waybills, value) => {
	if(Array.isArray(value)) {
		value.forEach(each => addInvoiceWaybill(waybills, each));
		return;
	}
	const waybill = normalizeInvoiceWaybill(value);
	if(!waybill || waybills.includes(waybill)) return;
	waybills.push(waybill);
};

const getInvoiceWaybills = (...sources) => {
	const waybills = [];

	for(const source of sources) {
		if(source === undefined || source === null || source === "") continue;
		if(typeof source === "string" || typeof source === "number") {
			addInvoiceWaybill(waybills, source);
			continue;
		}
		if(Array.isArray(source)) {
			getInvoiceWaybills(...source).forEach(waybill => addInvoiceWaybill(waybills, waybill));
			continue;
		}

		addInvoiceWaybill(waybills, source?.waybill);
		addInvoiceWaybill(waybills, source?.waybill_number);
	}

	return waybills;
};

const formatInvoiceWaybillNumbers = waybills => waybills.length > 0 ? waybills.join(", ") : "-";

const getSingleTripInvoiceWaybillNumber = trip => formatInvoiceWaybillNumbers(
	getInvoiceWaybills(
		trip?.destination,
		trip?.destination?.stops,
		trip?.destination?.stops?.stopovers,
		trip?.destination?.stopovers,
		trip?.stops,
		trip?.stopovers
	)
);

const getDetailedBusinessInvoiceWaybillNumber = trip => {
	return formatInvoiceWaybillNumbers(getInvoiceWaybills(trip?.waybills));
};

const getNumericInvoiceValue = value => {
	const parsedValue = Number(typeof value === "string" ? value.replace(/,/g, "") : value);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const INVOICE_TAX_NUMBER = "2522507230309";

const escapeCSVValue = value => `"${cleanInvoiceText(value).replace(/"/g, '""')}"`;

const formatInvoiceTripDate = value => {
	if(!value) return "";
	const invoiceTripDate = cleanInvoiceText(value);
	const storedDate = moment(invoiceTripDate, ["DD/MM/YY", "DD/MM/YYYY"], true);
	if(storedDate.isValid()) return storedDate.format("DD/MM/YY");
	const parsedDate = moment(invoiceTripDate);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YY") : invoiceTripDate;
};

const formatInvoiceDate = value => {
	const parsedDate = value ? moment(value) : moment();
	return `${parsedDate.format("MMMM").toUpperCase()} ${parsedDate.format("Do, YYYY")}`;
};

export const getTripInvoiceTotal = trip => {
	const extraStopAmount = getFirstAvailableValue(trip?.total_stop_amount, trip?.extra_stop_amount);
	return getNumericInvoiceValue(trip?.trip_amount) + getNumericInvoiceValue(extraStopAmount);
};

export const getTripPlatformCharge = trip => {
	const platformCharge = getFirstAvailableValue(trip?.platform_fee, trip?.platform_charge);
	return platformCharge === "-" ? "" : platformCharge;
};

// const getInvoiceLocationName = (...sources) => {
// 	for(const source of sources) {
// 		const locationName = cleanInvoiceText(getFirstAvailableValue(
// 			source?.state?.name,
// 			source?.state,
// 			source?.city?.name,
// 			source?.city,
// 			source?.location?.state,
// 			source?.location?.city,
// 			source?.address?.state,
// 			source?.address?.city
// 		));
// 		if(locationName) return locationName;
// 	}
// 	return "";
// };

// const getInvoiceLocationCode = (...sources) => {
// 	const locationName = getInvoiceLocationName(...sources).toUpperCase();
// 	if(!locationName) return "GEN";
// 	return INVOICE_LOCATION_CODES[locationName] || locationName.replace(/[^A-Z]/g, "").substring(0, 3).padEnd(3, "X");
// };

const getInvoiceSequence = (...sources) => {
	for(const source of sources) {
		const sequenceValue = getFirstAvailableValue(
			source?.invoice_sequence,
			source?.invoice_serial,
			source?.invoice_count,
			source?.serial_number,
			source?.serial,
			source?.sequence
		);
		const sequence = getNumericInvoiceValue(sequenceValue);
		if(sequence) return sequence;
	}

	for(const source of sources) {
		const reference = cleanInvoiceText(getFirstAvailableValue(
			source?.invoice_number,
			source?.invoice_ref,
			source?.invoice_reference,
			source?.reference,
			source?.id
		));
		const digits = reference.match(/\d+/g);
		if(digits) {
			const sequence = getNumericInvoiceValue(digits[digits.length - 1]);
			if(sequence) return sequence;
		}
	}

	return 1;
};

export const getTripInvoiceNumber = (...sources) => {
	// const organisation = sources.find(source => source?.reference || source?.organisation_reference || source?.org_reference) ?? {};
	// const orgReference = cleanInvoiceText(getFirstAvailableValue(
	// 	organisation?.invoice_reference_prefix,
	// 	organisation?.organisation_reference,
	// 	organisation?.org_reference,
	// 	organisation?.reference,
	// 	"TRUQ"
	// )).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
	// const locationCode = getInvoiceLocationCode(...sources);
	const year = moment().format("YYYY");
	const sequence = `${getInvoiceSequence(...sources)}`.padStart(4, "0");

	return `TRUQINV${year}${sequence}`;
};

export const getSingleTripInvoiceNumber = (trip, organisation = {}) => {
	return getTripInvoiceNumber(organisation, trip?.business_tripset, trip?.business, trip?.assigned_business, trip);
};

export const isNonBusinessSingleTrip = trip => {
	const businessTripset = trip?.business_tripset;
	if(businessTripset === null || businessTripset === "") return true;
	if(Array.isArray(businessTripset)) return businessTripset.length === 0;
	return typeof businessTripset === "object"
		&& businessTripset !== null
		&& Object.keys(businessTripset).length === 0;
};

export const getSingleTripValueAddedServices = trip => {
	if(!isNonBusinessSingleTrip(trip) || !trip?.extras || typeof trip.extras !== "object") return [];
	const quote = trip?.quote ?? {};

	return Object.entries(trip.extras).reduce((services, [key, quantity]) => {
		if(getNumericInvoiceValue(quantity) === 0 || !Object.prototype.hasOwnProperty.call(quote, key)) return services;
		const price = getNumericInvoiceValue(quote[key]);
		services.push({key, name: toTitleCase(key), price});
		return services;
	}, []);
};

export const formatSingleTripInvoiceData = (trip, organisation = {}, wallet = {}) => {
	const quote = trip?.quote ?? {};
	const isNonBusinessTrip = isNonBusinessSingleTrip(trip);
	const currency = getFirstAvailableValue(quote?.currency, organisation?.country?.currency_code, "NGN");
	const rawCharge = getFirstAvailableValue(quote?.charge, quote?.total_charge, trip?.charge);
	const rawVat = getFirstAvailableValue(quote?.vat_value, trip?.vat_value);
	const rawDiscountAmount = quote?.discount_amount;
	const rawServiceCharge = getFirstAvailableValue(quote?.platform_fee, trip?.platform_fee);
	const stopoverPrices = Array.isArray(trip?.destination?.stops?.stopovers)
		? trip.destination.stops.stopovers
			.map(stopover => stopover?.price)
			.filter(price => price !== undefined && price !== null && price !== "")
		: [];
	const rawExtraStopAmount = stopoverPrices.length > 0
		? stopoverPrices.reduce((sum, price) => sum + getNumericInvoiceValue(price), 0)
		: "";
	const valueAddedServices = getSingleTripValueAddedServices(trip);
	const vasCharge = valueAddedServices.reduce((sum, service) => sum + service.price, 0);
	const costOfServiceParts = [
		quote?.base_fare,
		getFirstAvailableValue(quote?.cost_per_km, quote?.cost_per_kilometer),
		quote?.extras_charge,
	];
	const costOfService = costOfServiceParts.reduce((sum, value) => sum + getNumericInvoiceValue(value), 0);
	const charge = getNumericInvoiceValue(rawCharge);
	const vat = getNumericInvoiceValue(rawVat);
	const discountAmount = getNumericInvoiceValue(rawDiscountAmount);
	const tripAmount = isNonBusinessTrip ? costOfService : charge - vat;
	const serviceCharge = getNumericInvoiceValue(rawServiceCharge);
	const extraStopAmount = getNumericInvoiceValue(rawExtraStopAmount);
	const hasCharge = rawCharge !== "";
	const hasVat = rawVat !== "";
	const hasDiscount = isNonBusinessTrip
		&& rawDiscountAmount !== undefined
		&& rawDiscountAmount !== null
		&& rawDiscountAmount !== "";
	const hasServiceCharge = rawServiceCharge !== "";
	const hasExtraStopAmount = rawExtraStopAmount !== "";
	const tripTotal = isNonBusinessTrip ? tripAmount + vasCharge : tripAmount;
	const subTotal = tripTotal;
	const grandTotal = charge;
	const invoiceDate = formatInvoiceDate();
	const tripDate = formatInvoiceTripDate(trip?.scheduled_date);
	const businessName = cleanInvoiceText(getFirstAvailableValue(
		trip?.business_tripset?.name,
		trip?.business_tripset?.business_name,
		trip?.business_tripset?.business?.name,
		trip?.business?.name,
		trip?.assigned_business?.name
	));
	const businessAddress = cleanInvoiceAddress(getFirstAvailableValue(
		trip?.business_tripset?.business_address,
		trip?.business_tripset?.address,
		trip?.business_tripset?.business?.address,
		trip?.business?.address,
		trip?.business?.location,
		trip?.assigned_business?.address,
		trip?.assigned_business?.location
	));
	const driverName = cleanInvoiceText(
		getFirstAvailableValue(
			`${trip?.driver?.user?.first_name ?? ""} ${trip?.driver?.user?.last_name ?? ""}`.trim(),
			`${trip?.driver?.first_name ?? ""} ${trip?.driver?.last_name ?? ""}`.trim()
		)
	);
	const plateNumber = cleanInvoiceText(getFirstAvailableValue(
		trip?.vehicle_payment?.vehicle_registration_number,
		trip?.vehicle?.dynamic_fields?.registration_number,
		trip?.driver?.assigned_vehicle?.dynamic_fields?.registration_number,
		trip?.driver?.vehicle?.dynamic_fields?.registration_number,
		trip?.registration_number,
		trip?.vehicle_registration_number
	));
	const vehicleTon = cleanInvoiceText(getFirstAvailableValue(
		trip?.vehicle?.category_size?.tonnage,
		trip?.driver?.assigned_vehicle?.category_size?.tonnage,
		trip?.category_size?.tonnage
	));
	const withdrawalConfigs = wallet?.withdrawal_configs ?? organisation?.withdrawal_configs ?? {};
	const accountName = cleanInvoiceText(getFirstAvailableValue(
		withdrawalConfigs?.account_name,
		organisation?.account_name
	));
	const bankName = cleanInvoiceText(getFirstAvailableValue(
		withdrawalConfigs?.bank,
		organisation?.bank
	));
	const accountNumber = cleanInvoiceText(getFirstAvailableValue(
		withdrawalConfigs?.account_number,
		organisation?.account_number
	));
	const checkoutUrl = cleanInvoiceText(quote?.checkout_url);
	const accountDetails = isNonBusinessTrip
		? checkoutUrl
		: [accountName, bankName, accountNumber].filter(Boolean).join("\n");
	const accountDetailsLabel = isNonBusinessTrip
		? "Checkout URL"
		: "Account Details (Name, Bank and Number)";
	const organisationAddress = cleanInvoiceAddress(getFirstAvailableValue(
		organisation?.location,
		organisation?.address
	));
	const supportEmail = cleanInvoiceText(organisation?.support_email);
	const supportPhone = isNonBusinessTrip ? "08144968867" : cleanInvoiceText(organisation?.support_phone);
	const contactDetails = [organisationAddress || "-", supportEmail, supportPhone].filter(Boolean).join("\n");

	return {
		logoText: "TRUQ",
		invoiceNumber: getSingleTripInvoiceNumber(trip, organisation),
		businessName,
		businessAddress,
		taxNumber: INVOICE_TAX_NUMBER,
		invoiceDate,
		tripDate,
		driverName,
		plateNumber,
		waybillNumber: getSingleTripInvoiceWaybillNumber(trip),
		pickupLocation: cleanInvoiceText(trip?.source?.address),
		dropoffLocation: cleanInvoiceText(trip?.destination?.address),
		vehicleTon,
		isNonBusinessTrip,
		accountDetails,
		accountDetailsLabel,
		checkoutUrl,
		contactDetails,
		currency,
		hasCharge,
		hasVat,
		hasDiscount,
		hasServiceCharge,
		hasExtraStopAmount,
		tripAmount,
		costOfService: tripAmount,
		valueAddedServices,
		vasCharge,
		extraStopAmount,
		tripTotal,
		subTotal,
		serviceCharge,
		vat,
		discountAmount,
		grandTotal,
	};
};

export const createSingleTripInvoiceDraft = (trip, organisation = {}, wallet = {}) => ({
	...formatSingleTripInvoiceData(trip, organisation, wallet),
	invoiceNumber: "",
	invoiceTemplate: trip?.invoice_template,
	sourceInvoiceBody: trip,
	trip,
});

export const applySavedInvoiceNumber = (invoice, invoiceId) => ({
	...invoice,
	invoiceNumber: cleanInvoiceText(invoiceId),
});

export const createSingleTripInvoiceSaveBody = invoice => ({
	...(invoice?.sourceInvoiceBody ?? invoice?.trip ?? {}),
	...(invoice?.invoiceTemplate ? {invoice_template: invoice.invoiceTemplate} : {}),
	invoice: {
		invoice_number: cleanInvoiceText(invoice?.invoiceNumber),
		business_name: cleanInvoiceText(invoice?.businessName),
		business_address: cleanInvoiceText(invoice?.businessAddress) || "-",
		tax_number: cleanInvoiceText(invoice?.taxNumber),
		invoice_date: cleanInvoiceText(invoice?.invoiceDate),
		trip_date: cleanInvoiceText(invoice?.tripDate),
		driver_name: cleanInvoiceText(invoice?.driverName),
		plate_number: cleanInvoiceText(invoice?.plateNumber),
		waybill_number: cleanInvoiceText(invoice?.waybillNumber),
		pickup_location: cleanInvoiceText(invoice?.pickupLocation),
		dropoff_location: cleanInvoiceText(invoice?.dropoffLocation),
		vehicle_ton: cleanInvoiceText(invoice?.vehicleTon),
		trip_amount: invoice?.tripAmount,
		cost_of_service: invoice?.costOfService,
		vas_charge: invoice?.vasCharge,
		value_added_services: invoice?.valueAddedServices,
		extra_stop_amount: invoice?.extraStopAmount,
		trip_total: invoice?.tripTotal,
		subtotal: invoice?.subTotal,
		service_charge: invoice?.serviceCharge,
		vat: invoice?.vat,
		...(invoice?.hasDiscount ? {discount_amount: invoice?.discountAmount} : {}),
		grand_total: invoice?.grandTotal,
	},
});

export const convertSingleTripInvoiceDraftToCSV = invoiceData => {
	const amountWithCurrency = (amount, shouldDisplay = true) => shouldDisplay ? `${invoiceData.currency} ${formatAmountToString(amount)}` : "";
	const isNonBusinessTrip = !!invoiceData.isNonBusinessTrip;
	const rows = [
		["TRUQ LOGO", invoiceData.logoText],
		["INVOICE NUMBER", invoiceData.invoiceNumber],
		["TAX NUMBER", invoiceData.taxNumber],
		["INVOICE DATE", invoiceData.invoiceDate],
		[],
		[
			"TRIP DATE",
			"PICKUP LOCATION",
			"DROPOFF LOCATION",
			"VEHICLE TON",
			isNonBusinessTrip ? "FREIGHT COST" : "COST OF SERVICE",
			...(isNonBusinessTrip ? ["V.A.S"] : []),
			"TRIP AMOUNT"
		],
		[
			invoiceData.tripDate,
			invoiceData.pickupLocation,
			invoiceData.dropoffLocation,
			invoiceData.vehicleTon,
			amountWithCurrency(invoiceData.tripAmount, invoiceData.hasCharge),
			...(isNonBusinessTrip ? [amountWithCurrency(invoiceData.vasCharge, invoiceData.hasCharge)] : []),
			amountWithCurrency(invoiceData.tripTotal, invoiceData.hasCharge)
		],
		[],
		...(isNonBusinessTrip ? [
			["VALUE ADDED SERVICES"],
			...(invoiceData.valueAddedServices ?? []).map(service => ([
				service.name,
				amountWithCurrency(service.price, invoiceData.hasCharge),
			])),
			["TOTAL V.A.S", amountWithCurrency(invoiceData.vasCharge, invoiceData.hasCharge)],
			[],
			["FREIGHT COST", amountWithCurrency(invoiceData.costOfService, invoiceData.hasCharge)],
			["VAS CHARGE", amountWithCurrency(invoiceData.vasCharge, invoiceData.hasCharge)],
		] : [
			["TOTAL TRIP AMOUNT", amountWithCurrency(invoiceData.subTotal, invoiceData.hasCharge)],
		]),
		["VAT", amountWithCurrency(invoiceData.vat, invoiceData.hasVat || invoiceData.hasCharge)],
		...(isNonBusinessTrip && invoiceData.hasDiscount ? [
			["DISCOUNT", amountWithCurrency(invoiceData.discountAmount)],
		] : []),
		["GRAND TOTAL", amountWithCurrency(invoiceData.grandTotal, invoiceData.hasCharge)],
		[],
		[isNonBusinessTrip ? "CHECKOUT URL" : "ACCOUNT DETAILS (NAME, BANK AND NUMBER)", invoiceData.accountDetails],
		["CONTACT (ADDRESS, EMAIL AND PHONE NUMBER)", invoiceData.contactDetails],
	];

	return rows.map(row => {
		if(row.length === 0) return "";
		return row.map(escapeCSVValue).join(",");
	}).join("\n");
};

export const convertSingleTripInvoiceToCSV = (trip, organisation = {}, wallet = {}) => (
	convertSingleTripInvoiceDraftToCSV(createSingleTripInvoiceDraft(trip, organisation, wallet))
);

export const getDetailedBusinessInvoiceNumber = (data = {}, organisation = {}) => {
	return getTripInvoiceNumber(organisation, data);
};

export const flattenDetailedBusinessInvoiceTrips = data => {
	let trips = [];
	if(!data || !Array.isArray(data.rates)) return trips;

	for(let rateIndex = 0; rateIndex < data.rates.length; rateIndex++) {
		const rate = data.rates[rateIndex];
		const tripRows = Array.isArray(rate?.tripsData) ? rate.tripsData : [];
		for(let tripIndex = 0; tripIndex < tripRows.length; tripIndex++) {
			const trip = tripRows[tripIndex];
			const waybill = getDetailedBusinessInvoiceWaybillNumber(trip);
			const rowKey = cleanInvoiceText(getFirstAvailableValue(
				trip?.waybill,
				trip?.reference,
				`${data?.reference ?? "INV"}-${rateIndex + 1}-${tripIndex + 1}`
			));
			const tripAmount = cleanInvoiceText(getFirstAvailableValue(trip?.trip_amount, trip?.charge));
			const extraStopAmount = cleanInvoiceText(trip?.total_stop_amount);
			trips.push({
				sourceTripData: trip,
				id: rowKey || `${rateIndex + 1}-${tripIndex + 1}`,
				selected: true,
				trip_date: formatInvoiceTripDate(getFirstAvailableValue(trip?.trip_date, rate?.date)),
				driver_name: cleanInvoiceText(trip?.driver_name),
				plate_number: cleanInvoiceText(getFirstAvailableValue(trip?.plate_number, trip?.registration_number)),
				waybill,
				pickup_location: cleanInvoiceText(trip?.pickup_location),
				dropoff_location: cleanInvoiceText(trip?.dropoff_location),
				vehicle_ton: cleanInvoiceText(trip?.tonnage),
				cost_of_service: cleanInvoiceText(getTripCostOfService(trip)),
				trip_amount: tripAmount,
				platform_charge: cleanInvoiceText(getTripPlatformCharge(trip)),
				extra_stop_amount: extraStopAmount,
				total_stop_amount: extraStopAmount,
				vat_type: cleanInvoiceText(trip?.vat_type),
				trip_total: getTripInvoiceTotal({trip_amount: tripAmount, extra_stop_amount: extraStopAmount}),
				vat: cleanInvoiceText(trip?.vat),
				charge: cleanInvoiceText(trip?.charge),
				day: cleanInvoiceText(getFirstAvailableValue(trip?.day, rate?.day)),
				category: cleanInvoiceText(trip?.category),
			});
		}
	}

	return trips;
};

const normalizeInvoiceTripFingerprintValue = value => `${value ?? ""}`.trim().toLowerCase().replace(/\s+/g, " ");

const getInvoiceTripFingerprint = trip => [
	trip?.trip_date,
	trip?.waybill,
	trip?.plate_number,
	trip?.pickup_location,
	trip?.dropoff_location,
	trip?.trip_amount,
	trip?.driver_name,
].map(normalizeInvoiceTripFingerprintValue).join("|");

const getInvoiceTripReference = trip => cleanInvoiceText(
	trip?.sourceTripData?.reference ?? trip?.reference
);

export const mergeSavedInvoiceTripSelections = (sourceTrips = [], savedTrips = []) => {
	const savedIndexesByReference = new Map();
	const referenceLessSavedIndexesByFingerprint = new Map();

	savedTrips.forEach((trip, index) => {
		const reference = getInvoiceTripReference(trip);
		const matches = reference
			? savedIndexesByReference
			: referenceLessSavedIndexesByFingerprint;
		const key = reference || getInvoiceTripFingerprint(trip);
		const indexes = matches.get(key) ?? [];
		indexes.push(index);
		matches.set(key, indexes);
	});

	const matchedSavedIndexes = new Set();
	const takeUnmatchedIndex = indexes => {
		while(indexes?.length > 0) {
			const index = indexes.shift();
			if(!matchedSavedIndexes.has(index)) return index;
		}
		return undefined;
	};
	const reconstructedTrips = sourceTrips.map((trip, index) => {
		const reference = getInvoiceTripReference(trip);
		let savedIndex = reference
			? takeUnmatchedIndex(savedIndexesByReference.get(reference))
			: undefined;
		if(savedIndex === undefined) {
			savedIndex = takeUnmatchedIndex(
				referenceLessSavedIndexesByFingerprint.get(getInvoiceTripFingerprint(trip))
			);
		}
		const selected = savedIndex !== undefined;
		if(selected) matchedSavedIndexes.add(savedIndex);
		return {
			...trip,
			id: `${trip?.id ?? "trip"}-${index}`,
			selected,
		};
	});

	savedTrips.forEach((trip, index) => {
		if(matchedSavedIndexes.has(index)) return;
		reconstructedTrips.push({
			...trip,
			id: `${trip?.id ?? "saved-trip"}-saved-${index}`,
			selected: true,
		});
	});
	return reconstructedTrips;
};

export const calculateDetailedBusinessInvoiceSummary = (trips = [], overrides = {}) => {
	const selectedTrips = trips.filter(each => each.selected);
	const subtotal = overrides.subtotal !== undefined && overrides.subtotal !== null && overrides.subtotal !== ""
		? getNumericInvoiceValue(overrides.subtotal)
		: selectedTrips.reduce((sum, trip) => sum + getTripInvoiceTotal(trip), 0);
	const defaultServiceCharge = subtotal * 0.1;
	const defaultVat = subtotal * 0.075;
	const serviceCharge = overrides.serviceCharge !== undefined && overrides.serviceCharge !== null && overrides.serviceCharge !== ""
		? getNumericInvoiceValue(overrides.serviceCharge)
		: defaultServiceCharge;
	const vat = overrides.vat !== undefined && overrides.vat !== null && overrides.vat !== ""
		? getNumericInvoiceValue(overrides.vat)
		: defaultVat;
	const platformCharge = overrides.platformCharge !== undefined
		&& overrides.platformCharge !== null
		&& overrides.platformCharge !== ""
		? getNumericInvoiceValue(overrides.platformCharge)
		: "";
	const grandTotal = overrides.grandTotal !== undefined && overrides.grandTotal !== null && overrides.grandTotal !== ""
		? getNumericInvoiceValue(overrides.grandTotal)
		: subtotal + vat;

	return {
		totalTrips: selectedTrips.length,
		subtotal,
		serviceCharge,
		vat,
		platformCharge,
		grandTotal,
		defaultServiceCharge,
		defaultVat,
	};
};

export const createDetailedBusinessInvoiceDraft = (data, organisation = {}, wallet = {}, options = {}) => {
	const trips = flattenDetailedBusinessInvoiceTrips(data);
	const rawSubtotal = getFirstAvailableValue(data?.sub_total, data?.new_sub_total);
	const backendSubtotal = rawSubtotal === "-" ? "" : rawSubtotal;
	const rawVat = getFirstAvailableValue(data?.vat, data?.new_vat);
	const backendVat = rawVat === "-" ? "" : rawVat;
	const rawPlatformCharge = getFirstAvailableValue(
		data?.new_platform_fee_total,
		data?.platform_fee_total,
		data?.platform_charge,
		data?.platformCharge
	);
	const backendPlatformCharge = rawPlatformCharge !== "-"
		&& rawPlatformCharge !== undefined
		&& rawPlatformCharge !== null
		&& rawPlatformCharge !== ""
		? rawPlatformCharge
		: "";
	const hasBackendSubtotal = backendSubtotal !== undefined && backendSubtotal !== null && backendSubtotal !== "";
	const hasBackendVat = backendVat !== "";
	const rawGrandTotal = options.useBackendGrandTotal
		? getFirstAvailableValue(data?.full_total, data?.new_full_total, data?.total, data?.new_total)
		: "";
	const backendGrandTotal = rawGrandTotal === "-" ? "" : rawGrandTotal;
	const hasBackendGrandTotal = backendGrandTotal !== "";
	const summary = calculateDetailedBusinessInvoiceSummary(trips, {
		subtotal: hasBackendSubtotal ? backendSubtotal : "",
		vat: hasBackendVat ? backendVat : "",
		platformCharge: backendPlatformCharge,
		grandTotal: hasBackendGrandTotal ? backendGrandTotal : "",
	});
	const withdrawalConfigs = wallet?.withdrawal_configs ?? organisation?.withdrawal_configs ?? {};
	const businessName = cleanInvoiceText(getFirstAvailableValue(data?.name, organisation?.name));
	const businessAddress = cleanInvoiceAddress(getFirstAvailableValue(
		data?.address,
		data?.location
	));
	const organisationAddress = cleanInvoiceAddress(getFirstAvailableValue(
		organisation?.location,
		organisation?.address
	));
	const accountDetails = [
		cleanInvoiceText(getFirstAvailableValue(withdrawalConfigs?.account_name, organisation?.account_name)),
		cleanInvoiceText(getFirstAvailableValue(withdrawalConfigs?.bank, organisation?.bank)),
		cleanInvoiceText(getFirstAvailableValue(withdrawalConfigs?.account_number, organisation?.account_number)),
	].filter(Boolean).join("\n");
	const contactDetails = [
		organisationAddress || "-",
		cleanInvoiceText(organisation?.support_email),
		cleanInvoiceText(organisation?.support_phone),
	].filter(Boolean).join("\n");

	return {
		invoiceTemplate: data?.invoice_template,
		logoText: "TRUQ",
		reference: cleanInvoiceText(data?.reference),
		period: cleanInvoiceText(data?.period),
		currency: cleanInvoiceText(getFirstAvailableValue(data?.currency, organisation?.country?.currency_code, "NGN")),
		invoiceNumber: "",
		businessName,
		businessAddress,
		taxNumber: INVOICE_TAX_NUMBER,
		invoiceDate: formatInvoiceDate(data?.date),
		accountDetails,
		contactDetails,
		trips,
		subtotal: summary.subtotal,
		serviceCharge: summary.serviceCharge,
		vat: summary.vat,
		platformCharge: summary.platformCharge,
		grandTotal: summary.grandTotal,
		serviceChargeEdited: false,
		vatEdited: hasBackendVat,
		grandTotalEdited: hasBackendGrandTotal,
		sourceInvoiceBody: data,
	};
};

export const createDetailedBusinessInvoiceSaveBody = invoice => {
	const summary = calculateDetailedBusinessInvoiceSummary(invoice?.trips ?? [], {
		subtotal: invoice?.subtotal,
		serviceCharge: invoice?.serviceChargeEdited ? invoice?.serviceCharge : "",
		vat: invoice?.vatEdited ? invoice?.vat : "",
		platformCharge: invoice?.platformCharge,
		grandTotal: invoice?.grandTotalEdited ? invoice?.grandTotal : "",
	});
	const selectedTrips = (invoice?.trips ?? []).filter(each => each.selected);
	const sourceInvoiceBody = invoice?.sourceInvoiceBody ?? {};

	return {
		...sourceInvoiceBody,
		...(invoice?.invoiceTemplate ? {invoice_template: invoice.invoiceTemplate} : {}),
		name: cleanInvoiceText(invoice?.businessName),
		address: cleanInvoiceText(invoice?.businessAddress) || sourceInvoiceBody?.address,
		reference: cleanInvoiceText(invoice?.reference),
		period: cleanInvoiceText(invoice?.period),
		sub_total: summary.subtotal,
		total: invoice?.grandTotalEdited ? summary.grandTotal : summary.subtotal,
		vat: summary.vat,
		platform_fee_total: summary.platformCharge,
		platform_charge: summary.platformCharge,
		full_total: summary.grandTotal,
		total_trips: selectedTrips.length,
		rates: [
			{
				date: cleanInvoiceText(invoice?.invoiceDate),
				day: "",
				trips: selectedTrips.length,
				amount: summary.subtotal,
				tripsData: selectedTrips.map(trip => ({
					...(trip?.sourceTripData ?? {}),
					waybill: cleanInvoiceText(trip?.sourceTripData?.waybill ?? trip?.waybill),
					charge: cleanInvoiceText(trip?.charge),
					vat: cleanInvoiceText(trip?.vat),
					platform_fee: cleanInvoiceText(getTripPlatformCharge(trip)),
					platform_charge: cleanInvoiceText(getTripPlatformCharge(trip)),
					pickup_location: cleanInvoiceText(trip?.pickup_location),
					dropoff_location: cleanInvoiceText(trip?.dropoff_location),
					driver_name: cleanInvoiceText(trip?.driver_name),
					trip_date: cleanInvoiceText(trip?.trip_date),
					plate_number: cleanInvoiceText(trip?.plate_number),
					cost_of_service: cleanInvoiceText(getTripCostOfService(trip)),
					trip_amount: cleanInvoiceText(trip?.trip_amount),
					extra_stop_amount: cleanInvoiceText(trip?.extra_stop_amount),
					total_stop_amount: cleanInvoiceText(getFirstAvailableValue(trip?.total_stop_amount, trip?.extra_stop_amount)),
					vat_type: cleanInvoiceText(trip?.vat_type),
					tonnage: cleanInvoiceText(trip?.vehicle_ton),
					category: cleanInvoiceText(trip?.category),
				})),
			}
		],
	};
};

const getDetailedBusinessInvoiceRows = invoice => {
	const summary = calculateDetailedBusinessInvoiceSummary(invoice?.trips ?? [], {
		subtotal: invoice?.subtotal,
		serviceCharge: invoice?.serviceChargeEdited ? invoice?.serviceCharge : "",
		vat: invoice?.vatEdited ? invoice?.vat : "",
		platformCharge: invoice?.platformCharge,
		grandTotal: invoice?.grandTotalEdited ? invoice?.grandTotal : "",
	});
	const selectedTrips = (invoice?.trips ?? []).filter(each => each.selected);
	const currency = cleanInvoiceText(getFirstAvailableValue(invoice?.currency, "NGN"));
	const amountWithCurrency = value => `${currency} ${formatAmountToString(getNumericInvoiceValue(value))}`;

	return [
		["TRUQ LOGO", cleanInvoiceText(invoice?.logoText)],
		["INVOICE NUMBER", cleanInvoiceText(invoice?.invoiceNumber)],
		["BUSINESS NAME", cleanInvoiceText(invoice?.businessName)],
		["ADDRESS OF BUSINESS", cleanInvoiceText(invoice?.businessAddress) || "-"],
		["TAX NUMBER", cleanInvoiceText(invoice?.taxNumber)],
		["INVOICE DATE", cleanInvoiceText(invoice?.invoiceDate)],
		["PERIOD", cleanInvoiceText(invoice?.period)],
		[],
		[
			"TRIP DATE",
			"DRIVER'S NAME",
			"PLATE NUMBER",
			"WAYBILL NUMBER",
			"PICKUP LOCATION",
			"DROPOFF LOCATION",
			"VEHICLE TON",
			"COST OF SERVICE",
			// "V.A.T VALUE",
			"PLATFORM FEE",
			"EXTRA STOP AMOUNT",
			"TRIP AMOUNT"
		],
		...selectedTrips.map(trip => ([
			cleanInvoiceText(trip.trip_date),
			cleanInvoiceText(trip.driver_name),
			cleanInvoiceText(trip.plate_number),
			cleanInvoiceText(trip.waybill),
			cleanInvoiceText(trip.pickup_location),
			cleanInvoiceText(trip.dropoff_location),
			cleanInvoiceText(trip.vehicle_ton),
			getTripCostOfService(trip) !== "" ? amountWithCurrency(getTripCostOfService(trip)) : "-",
			// trip.vat !== "" ? amountWithCurrency(trip.vat) : "-",
			getTripPlatformCharge(trip) !== "" ? amountWithCurrency(getTripPlatformCharge(trip)) : "-",
			trip.extra_stop_amount !== "" ? amountWithCurrency(trip.extra_stop_amount) : "-",
			amountWithCurrency(getTripInvoiceTotal(trip)),
		])),
		[],
		["TOTAL TRIP AMOUNT", amountWithCurrency(summary.subtotal)],
		["PLATFORM FEE", summary.platformCharge !== "" ? amountWithCurrency(summary.platformCharge) : "-"],
		["VAT", amountWithCurrency(summary.vat)],
		["GRAND TOTAL", amountWithCurrency(summary.grandTotal)],
		[],
		["ACCOUNT DETAILS (NAME, BANK AND NUMBER)", cleanInvoiceText(invoice?.accountDetails)],
		["CONTACT (ADDRESS, EMAIL AND PHONE NUMBER)", cleanInvoiceText(invoice?.contactDetails)],
	];
};

export const exportDetailedBusinessInvoiceToExcel = (invoice, fileName) => {
	const rows = getDetailedBusinessInvoiceRows(invoice);
	const worksheet = utils.aoa_to_sheet(rows);
	const workbook = utils.book_new();
	utils.book_append_sheet(workbook, worksheet, "Invoice");
	writeFile(workbook, fileName);
};

export const formatTripDetails = (trip) => {
	let newArray = [], totalExtrasCharge = 0;

	trip.invoice_type = trip.reference ? "RECEIPT" : "INVOICE";
	if(!trip.reference) trip.reference = "NO REFERENCE";
	if(!trip.user) trip.user = {first_name: '', last_name: ''};
	let currency = (!trip.quote.currency) ? "NGN" : trip.quote.currency;

	if(!trip["business_tripset"]) {
		for(const eachItem in trip["extras"]) {
			if(trip["extras"][eachItem] !== 0) {
				if(eachItem in trip.quote) {
					let entryKey = toTitleCase(eachItem);
					let eachEntryVal = [`${currency} ${formatAmountToString(Number(trip.quote[eachItem]))}`];
					let entry = [entryKey, eachEntryVal];
					newArray.push(entry);
					totalExtrasCharge += Number(trip.quote[eachItem]);
				}
			}
		}

		let insuranceEntry = [["Insurance"], [`${currency} ${formatAmountToString(Number(trip.quote["estimated_insurance"]))}`]];
		newArray.push(insuranceEntry);
	}


	let tripKey = [`Trip to destination (${trip.category_size.tonnage} tonne ${trip.category_size.category})`];
	let tripAmount = Number(trip.quote["total_charge"]) - (Number(totalExtrasCharge) + Number(trip.quote["vat_value"]));
	if(trip.quote["estimated_insurance"]) tripAmount -= Number(trip.quote["estimated_insurance"]);

	let tripValue = [`${currency} ${formatAmountToString(tripAmount)}`];
	let tripEntry = [tripKey, tripValue];
	newArray.push(tripEntry);

	let vatEntry = [["VAT"], [`${currency} ${formatAmountToString(Number(trip.quote["vat_value"]))}`]];
	newArray.push(vatEntry);

	if(trip.quote['discount_amount']) {
		let discountEntry = [["Discount"], [`${currency} ${formatAmountToString(Number(trip.quote['discount_amount']))}`]];
		newArray.push(discountEntry);
	}

	return newArray;
}

export const formatIMSTripDetails = (trip, picklist) => {
	let newArray = [], stopOversArray = [], sumTotalsArray = [],
		totalReturned = [], totalReconciled = [], totalValuation = [], totalPrice = [], totalQuantity = [];

	trip.invoice_type = trip.reference ? "RECEIPT" : "INVOICE";
	if(!trip.reference) trip.reference = "NO REFERENCE";
	if(!trip.user) trip.user = {first_name: '', last_name: ''};
	let currency = (!trip.quote.currency) ? "NGN" : trip.quote.currency;

	if(picklist && picklist.length > 0) {
		let picklistHeader = [
			[
				{text: 'Delivery ID', style: 'tableMainHeader', alignment: 'center'},
				{text: 'Store', style: 'tableMainHeader', alignment: 'center'},
				{text: 'Breakdown', style: 'tableMainHeader', colSpan: 6, alignment: 'center'},
				{},
				{},
				{},
				{},
				{},
			],
			[
				{},
				{},
				{text: 'Product', style: 'tableHeader', alignment: 'center'},
				{text: 'Unit Price', style: 'tableHeader', alignment: 'center'},
				{text: 'Quantity', style: 'tableHeader', alignment: 'center'},
				{text: 'Total Price', style: 'tableHeader', alignment: 'center'},
				{text: 'Reconciled', style: 'tableHeader', alignment: 'center'},
				{text: 'Returned', style: 'tableHeader', alignment: 'center'},
			]
		];

		for(let eachItem of picklist) {
			let store = (eachItem['merchant'] && eachItem["merchant"]["extras"] && eachItem["merchant"]["extras"]["name"])
				? eachItem["merchant"]["extras"]["name"] : "";
			for(let productItem of eachItem['products']) {
				let id = productItem['order_ref'] ?? "";
				let product = productItem['name'] ? productItem['name'] : productItem['sku'] ? productItem['sku'] : '-';
				let price = productItem['price'] ?? '0';
				let quantity = productItem['quantity'] ?? '0';
				let valuation = productItem['valuation'] ?? '0';
				let reconciled = productItem['reconciled'] ?? '0';
				let returned = productItem['returned'] ?? '0';
				let entry = [
					id,
					store,
					product,
					`${eachItem['currency']} ${formatAmountToString(price)}`,
					formatNumber(quantity),
					`${eachItem['currency']} ${formatAmountToString(valuation)}`,
					formatNumber(reconciled),
					formatNumber(returned)
				];

				stopOversArray.push(entry);
				totalPrice = [...totalPrice, price];
				totalReturned = [...totalReturned, returned];
				totalQuantity = [...totalQuantity, quantity];
				totalReconciled = [...totalReconciled, reconciled];
			}

			totalValuation = [...totalValuation, eachItem['valuation']];
		}

		let sumTotalPrice = totalPrice.reduce((accumulator, value) => Number(accumulator) + Number(value), 0);
		let sumTotalQuantity = totalQuantity.reduce((accumulator, value) => Number(accumulator) + Number(value), 0);
		let sumTotalValuation = totalValuation.reduce((accumulator, value) => Number(accumulator) + Number(value), 0);
		let sumTotalReturned = totalReturned.reduce((accumulator, value) => Number(accumulator) + Number(value), 0);
		let sumTotalReconciled = totalReconciled.reduce((accumulator, value) => Number(accumulator) + Number(value), 0);

		sumTotalsArray = [
			{colSpan: 3, text: 'TOTAL', style: 'tableHeader', alignment: 'left'},
			{},
			{},
			{text: `${currency} ${formatAmountToString(sumTotalPrice)}`, style: 'tableHeader', alignment: 'center'},
			{text: formatNumber(sumTotalQuantity), style: 'tableHeader', alignment: 'center'},
			{text: `${currency} ${formatAmountToString(sumTotalValuation)}`, style: 'tableHeader', alignment: 'center'},
			{text: formatNumber(sumTotalReconciled), style: 'tableHeader', alignment: 'center'},
			{text: formatNumber(sumTotalReturned), style: 'tableHeader', alignment: 'center'},
		]

		stopOversArray = [...picklistHeader, ...stopOversArray, sumTotalsArray];
	}

	let tripKey = [`Trip to destination (${trip.category_size.tonnage} tonne ${trip.category_size.category})`];
	let tripAmount = Number(trip.quote["total_charge"]) - Number(trip.quote["vat_value"]);
	let tripValue = [`${currency} ${formatAmountToString(tripAmount)}`];
	let tripEntry = [tripKey, tripValue];
	newArray.push(tripEntry);

	let vatEntry = [["VAT"], [`${currency} ${formatAmountToString(Number(trip.quote["vat_value"]))}`]];
	newArray.push(vatEntry);

	if(trip.quote['discount_amount']) {
		let discountEntry = [["Discount"], [`${currency} ${formatAmountToString(Number(trip.quote['discount_amount']))}`]];
		newArray.push(discountEntry);
	}
	return {quoteArray: newArray, stopOversArray};
}

export const formatWebsite = website => {
	if(/(http(s?)):\/\//i.test(website)) return website;
	else return `https://${website}`;
}

export const formatSupportedCountries = payloadData => {
	return payloadData.map(item => ({...item, label: item.name, value: item.country_code}));
};

export const formatServices = payloadData => {
	return payloadData.map(item => ({...item, label: item.name, value: item.reference}));
};

export const formatFleetCategories = payloadData => {
	return payloadData.map(item => ({...item, label: `${item.category} - ${item.tonnage} tonne`, value: item.tonnage}));
};

export const formatFleet = payloadData => {
	return payloadData.map(item => ({
		...item,
		label: `${item.dynamic_fields['registration_number'].toUpperCase()} - ${item.category_size.category.toUpperCase()} (${item.category_size.tonnage} tonne)`,
		value: item.id
	}));
};

export const formatRoles = payloadData => {
	return payloadData.map(item => ({...item, label: item.name, value: item.id}));
};

export const formatPermissions = payloadData => {
	let newObj = {};
	for(let permissionsEnumKey in PermissionsEnum) {
		const ifExist = payloadData.find(item => item.slug === PermissionsEnum[permissionsEnumKey]);
		if(ifExist) newObj[PermissionsEnum[permissionsEnumKey]] = true;
	}
	return newObj
};

export const formatGooglePredictions = payloadData => {
	return payloadData.map(item => ({label: item.name, value: item['place_id'], location: {address: item.name}}))
};

export const customStringGenerator = (characters, size) => {
	let result = "", charactersLength = characters.length;
	for(let i = 0; i < size; i++) result += characters.charAt(Math.floor(Math.random() * charactersLength));
	return result;
};

export const passwordGenerator = () => {
	let suffix = customStringGenerator('1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZ@#$!%^&+=;', 8);
	return `Aa1;${suffix}`;
};

export const fileToBase64 = event => {
	let files;
	return new Promise(resolve => {
		if(event.dataTransfer) files = event.dataTransfer.files;
		else if(event.target) files = event.target.files;
		const reader = new FileReader();
		reader.onload = () => resolve(reader['result']);
		reader.readAsDataURL(files[0]);
	});
};

export const processSheet = file => {
	const reader = new FileReader();
	return new Promise(resolve => {
		reader.onload = event => {
			const wb = read(event.target.result);
			const sheets = wb.SheetNames;
			if(sheets.length) resolve(utils.sheet_to_json(wb.Sheets[sheets[0]]))
		}
		reader.readAsArrayBuffer(file);
	});
}

export const getBase64ImageFromURL = (url) => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.setAttribute("crossOrigin", "anonymous");
		img.onload = () => {
			const canvas = document.createElement("canvas");
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext("2d");
			ctx.drawImage(img, 0, 0);
			const dataURL = canvas.toDataURL("image/png");
			resolve(dataURL);
		};
		img.onerror = (error) => reject(error);
		img.src = url;
	});
};

export const convertBusinessInvoiceToCSV = (data, currency, type) => {
	const invoiceRows = formatBusinessInvoiceRate(data, currency, type);
	const json2CSV = `\n${invoiceRows.map(row => row.map(escapeCSVValue).join(",")).join("\n")}\n`;

	return [
		'\n',
		`BUSINESS: ${data['name']}\n`,
		`BUSINESS REFERENCE: ${data['reference']}\n`,
		`DURATION: ${data['period']}\n`,
		`TOTAL NUMBER OF TRIPS: ${data['total_trips']}\n`,
		`TOTAL NUMBER OF TRIPS: ${data['total_trips']}\n`,
		`TOTAL NUMBER OF TRIPS: ${data['total_trips']}\n`,
		`VALUE ADDED TAX: ${data['vat']}\n`,
		`TOTAL COST OF TRIPS: ${data['total']}\n`,
		'\n', json2CSV
	];
};

export const flattenObject = (obj) => {
	let result = {};

	for(const key in obj) {

		if(!obj[key]) result[key] = "_"
		else if(typeof obj[key] === "object" && !Array.isArray(obj[key])) {
			let subFlatObject = flattenObject(obj[key]);
			for(const subkey in subFlatObject) {
				if(!subFlatObject[subkey]) result[key + "_" + subkey] = "_"
				else result[key + "_" + subkey] = subFlatObject[subkey];
			}
		} else {
			result[key] = obj[key];
		}

	}
	return result;
};

export const convertJSONtoCSV = async(payload) => {
	/*
	const replacer = (key, value) => value === null ? '' : value;
		let header = Object.keys(arr[0]);
		let csv = arr.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','));
		header = Object.keys(header).map(key => toTitleCase(header[key]).toUpperCase())
		csv.unshift(header.join(','));
		return csv.join('\r\n'); */
	let data = "", header;
	for(let i = 0; i < payload.length; i++) {
		let result = flattenObject(payload[i]);
		let row = '';

		for(const index in result) {
			if(row !== '') row += ',';
			row += `"${result[index].toString()}"`;
		}

		if(i === 0) header = Object.keys(result);
		data += `${row}\r\n`;
	}
	header = Object.keys(header).map(key => toTitleCase(header[key]).toUpperCase());
	return `${header}\r\n${data}`;
};


export const exportData = (data, fileName, type) => {
	const blob = new Blob([data], {type});
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	a.click();
	window.URL.revokeObjectURL(url);
}

export const classHandler = size => {
	let gridName = {card: "col-3", grid: "col-xl-12 col-lg-12 col-sm-12"};
	if(size === 1) gridName = {card: "col-md-6", grid: "col-xl-6 col-lg-6 col-md-6 col-sm-12"};
	else if(size === 2) gridName = {card: "col-md-9", grid: "col-xl-4 col-lg-4 col-md-4 col-sm-12"};
	else if(size === 3) gridName = {card: "col-md-12", grid: "col-xl-3 col-lg-3 col-md-3 col-sm-12"};
	else if(size === 4) gridName = {card: "col-md-12", grid: "col-xl-3 col-lg-3 col-md-3 col-sm-12"};
	else if(size >= 4) gridName = {card: "col-md-12", grid: "col-xl-3 col-lg-3 col-md-4 col-sm-12"};
	return gridName;
};

export const gridHandler = size => {
	let gridName = "col-xl-3 col-lg-3 col-md-3 col-sm-3";
	if(size === 1) gridName = "col-xl-3 col-lg-3 col-md-3 col-sm-3";
	else if(size === 2) gridName = "col-xl-3 col-lg-3 col-md-3 col-sm-12";
	else if(size === 3) gridName = "col-xl-3 col-lg-3 col-md-3 col-sm-12";
	else if(size >= 4) gridName = "col-xl-3 col-lg-3 col-md-3 col-sm-3";
	return gridName;
};

export const formatAmountToString = data => {
	const format = !data ? "0.00" : data;
	if(format.toString() === "0.00") return format.toLocaleString();
	else if(format.toString().includes(",")) {
		const newFormat = format.toString().replace(/,/g, "");
		const formattedNumber = parseFloat(newFormat);
		return formattedNumber.toLocaleString();
	} else if(format.toString().includes(".")) {
		const formatValue = truncateDecimalValue(format);
		return formatValue.toLocaleString();
	} else return `${format.toLocaleString()}`;
};

export const truncateDecimalValue = value => {
	value = value.toString();
	const numberValue = value.split(".")[0];
	const suffixedNumber = value.split(".")[1].substring(0, 2);
	const decimalCount = value.split(".")[1].length;
	const prefixedNumber = Number(numberValue).toLocaleString();
	if(decimalCount > 2) value = `${prefixedNumber}.${suffixedNumber}`;
	else if(decimalCount < 2) value = `${prefixedNumber}.${suffixedNumber}0`;
	else value = suffixedNumber === "00" ? prefixedNumber : `${prefixedNumber}.${suffixedNumber}`;
	return value;
};

export const getBusinessExtensiveInsight = data => {
	let businesses = [], businessMap = [];
	if(!data) return [];

	for(let eachMonth of data) {
		for(let business of eachMonth.business) {
			if(businessMap.indexOf(business.business) === -1) {
				businessMap.push(business.business);
				businesses.push({reference: business.business, name: business.name});
			}
		}
	}
	return businesses;
};

export const extractBusinessDataInsight = (data, reference) => {
	let businessData = [];
	if(!data) return [];

	for(let each of data) {
		let businessInMonth = each.business.find(eachMap => eachMap.business === reference);
		let monthData = {month: each.month}
		monthData.trips = businessInMonth && businessInMonth.count ? businessInMonth.count : 0;
		monthData.gross = businessInMonth && businessInMonth.gross ? formatAmountToString(businessInMonth.gross) : 0;
		monthData.net = businessInMonth && businessInMonth.net ? formatAmountToString(businessInMonth.net) : 0;
		businessData.push(monthData);
	}
	return businessData;
};


export const getTripTypeExtensiveInsight = data => {
	let tripTypes = [], tripMap = [];
	if(!data) return [];

	for(let item of data) {
		for(let tripType of item.trips) {
			if(tripMap.indexOf(tripType.key) === -1) {
				tripMap.push(tripType.key);
				tripTypes.push({
					key: tripType.key,
					customized_name: tripType.customized_name,
					system_name: tripType.system_name
				});
			}
		}
	}
	return tripTypes;
};

export const getUserJourneyDates = (payload) => {
	let userJourney = {};
	payload["user_journey"].map(item => (userJourney = { ...userJourney, [item["system_status"]]: item["time"] }));
	return userJourney
};
